import {
    FETCH_MESSAGES,
    FETCH_MESSAGES_SUCCESS,
    FETCH_MESSAGES_FAILED,
    SEND_MESSAGE,
    STOP_FETCH_MESSAGES
} from "../store/types";
import store from '../store/store';
import { firebase } from '../config/configureFirebase';
import { RequestPushMsg } from '../other/NotificationFunctions';
import { onValue, off, get, child, ref as dbRef } from "firebase/database";
import { safePush, safeRead, safeUpdate } from '../other/firebaseDbHelper';

const chatPollers = {};

const getChatMessagesRef = (bookingId) => {
    if (typeof firebase?.chatRef === 'function') {
        return firebase.chatRef(bookingId);
    }

    if (firebase?.database) {
        return dbRef(firebase.database, 'chats/' + bookingId + '/messages');
    }

    return null;
};

const normalizeMessages = (rootEntry) => {
    if (!rootEntry) {
        return null;
    }

    const allMesseges = [];
    for (let key in rootEntry) {
        let entryKey = rootEntry[key];
        for (let msgKey in entryKey) {
            entryKey[msgKey].smsId = msgKey;
            entryKey[msgKey].chatId = key;
            allMesseges.push(entryKey[msgKey]);
        }
    }

    return allMesseges;
};

const dispatchMessages = (dispatch, rootEntry) => {
    const allMesseges = normalizeMessages(rootEntry);

    if (allMesseges) {
        dispatch({
            type: FETCH_MESSAGES_SUCCESS,
            payload: allMesseges,
        });
    } else {
        dispatch({
            type: FETCH_MESSAGES_FAILED,
            payload: store.getState().languagedata.defaultLanguage.chat_not_found,
        });
    }
};

const clearChatPolling = (bookingId) => {
    if (chatPollers[bookingId]) {
        clearInterval(chatPollers[bookingId]);
        delete chatPollers[bookingId];
    }
};

const startChatPolling = (bookingId, dispatch) => {
    clearChatPolling(bookingId);

    const fetchViaRest = () => {
        safeRead(`/chats/${bookingId}/messages`)
            .then((data) => {
                dispatchMessages(dispatch, data);
            })
            .catch(() => {
                dispatch({
                    type: FETCH_MESSAGES_FAILED,
                    payload: store.getState().languagedata.defaultLanguage.chat_not_found,
                });
            });
    };

    fetchViaRest();
    chatPollers[bookingId] = setInterval(fetchViaRest, 2000);
};

const getLiveRecipientToken = async (uid) => {
    const { singleUserRef } = firebase;

    if (!uid) {
        return null;
    }

    try {
        const snapshot = await get(singleUserRef(uid));
        const profile = snapshot.val();
        const token = profile?.pushToken;

        if (!token || token === 'token_error' || token === 'web') {
            return null;
        }

        return token;
    } catch (error) {
        console.error('[chat-push] Failed to fetch live recipient token.', {
            uid,
            error: error?.message || error,
        });
        return null;
    }
};

const sendChatPushNotification = async (data) => {
    const isCustomer = data.role == 'customer';
    const recipientToken = isCustomer ? data.booking?.driver_token : data.booking?.customer_token;
    const recipientId = isCustomer ? data.booking?.driver : data.booking?.customer;
    const recipientLabel = isCustomer ? 'driver' : 'customer';

    let tokenToUse = recipientToken;

    if (!tokenToUse) {
        console.warn(`[chat-push] Missing ${recipientLabel} token for chat notification.`, {
            bookingId: data.booking?.id,
            senderRole: data.role,
            driverId: data.booking?.driver,
            customerId: data.booking?.customer,
        });
        tokenToUse = await getLiveRecipientToken(recipientId);

        if (tokenToUse) {
            console.log('[chat-push] Recovered recipient token from live profile.', {
                bookingId: data.booking?.id,
                senderRole: data.role,
                recipientId,
                recipientLabel,
            });
        }
    }

    if (!tokenToUse) {
        console.warn('[chat-push] Unable to send chat notification because recipient token is still missing after fallback.', {
            bookingId: data.booking?.id,
            senderRole: data.role,
            recipientId,
            recipientLabel,
        });
        return;
    }

    RequestPushMsg(
        tokenToUse,
        {
            title: store.getState().languagedata.defaultLanguage.notification_title + store.getState().languagedata.defaultLanguage.chat_requested,
            msg: data.message,
            screen: 'onlineChat',
            params: { bookingId: data.booking.id }
        }
    );
};

export const fetchChatMessages = (bookingId) => (dispatch) => {
    const messagesRef = getChatMessagesRef(bookingId);
  
    dispatch({
        type: FETCH_MESSAGES,
        payload: bookingId,
    });

    if (!messagesRef) {
        dispatch({
            type: FETCH_MESSAGES_FAILED,
            payload: store.getState().languagedata.defaultLanguage.chat_not_found,
        });
        return;
    }

    if (!firebase?.auth?.currentUser) {
        startChatPolling(bookingId, dispatch);
        return;
    }

    clearChatPolling(bookingId);

    onValue(
        messagesRef,
        (snapshot) => {
            dispatchMessages(dispatch, snapshot.val());
        },
        () => {
            dispatch({
                type: FETCH_MESSAGES_FAILED,
                payload: store.getState().languagedata.defaultLanguage.chat_not_found,
            });
        }
    );
};

export const sendMessage = (data) => (dispatch) => {
    const messagesRef = getChatMessagesRef(data.booking.id);
    const useRealtimeAuth = !!firebase?.auth?.currentUser;
    console.log('[chat-send] sendMessage called.', {
        bookingId: data.booking?.id || null,
        role: data.role || null,
        customerId: data.booking?.customer || null,
        driverId: data.booking?.driver || null,
        messageLength: data.message?.length || 0,
        hasMessagesRef: !!messagesRef,
        useRealtimeAuth,
    });

    const chatId = data.booking.customer + ','  + data.booking.driver
    var today = new Date();
    var time = today.getHours() + ":" + today.getMinutes();
    var dd = String(today.getDate()).padStart(2, '0');
    var mm = String(today.getMonth() + 1).padStart(2, '0'); 
    var yyyy = today.getFullYear();
    today = mm + ':' + dd + ':' + yyyy;
    
    const msg = {
        message:data.message,
        from: data.role == 'customer'? data.booking.customer : data.booking.driver,
        type:"msg",
        msgDate:today,
        msgTime:time,
        createdAt: new Date().toString(),
        source: data.role,
        readBy: {
            [data.role == 'customer' ? data.booking.customer : data.booking.driver]: new Date().getTime(),
        },
    };

    if (!messagesRef) {
        console.warn('[chat-send] Missing messagesRef. Message send skipped.', {
            bookingId: data.booking?.id || null,
        });
        return;
    }

    safePush(child(messagesRef, chatId), msg);
    console.log('[chat-send] Message pushed to realtime DB.', {
        bookingId: data.booking?.id || null,
        chatId,
    });
    sendChatPushNotification(data);

    if (!useRealtimeAuth) {
        dispatch({
            type: SEND_MESSAGE,
            payload: msg,
        });
    }
}

export const markChatMessagesRead = (bookingId, role) => async () => {
    const messagesRef = getChatMessagesRef(bookingId);
    const uid = store.getState().auth.profile?.uid;

    if (!messagesRef || !bookingId || !role || !uid) {
        return;
    }

    let rootEntry = null;

    try {
        if (firebase?.auth?.currentUser) {
            const snapshot = await get(messagesRef);
            rootEntry = snapshot.val();
        } else {
            rootEntry = await safeRead(`/chats/${bookingId}/messages`);
        }
    } catch {
        return;
    }

    if (!rootEntry) {
        return;
    }

    const readAt = new Date().getTime();
    const updates = [];

    for (let chatId in rootEntry) {
        const messages = rootEntry[chatId];
        for (let msgKey in messages) {
            const msg = messages[msgKey];
            if (msg?.source && msg.source !== role && !msg.readBy?.[uid]) {
                updates.push(safeUpdate(child(messagesRef, `${chatId}/${msgKey}/readBy`), { [uid]: readAt }));
            }
        }
    }

    await Promise.all(updates);
};

export const stopFetchMessages = (bookingId) => (dispatch) => {
    const messagesRef = getChatMessagesRef(bookingId);

    dispatch({
        type: STOP_FETCH_MESSAGES,
        payload: bookingId,
    });

    clearChatPolling(bookingId);

    if (messagesRef) {
        off(messagesRef);
    }
}
